/**
 * Analytics utility for tracking conversion funnel events
 * Sends events to both PostHog (if available) and backend
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session/anonymous ID management
const getOrCreateSessionId = () => {
  let sessionId = sessionStorage.getItem('aasc_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem('aasc_session_id', sessionId);
  }
  return sessionId;
};

const getOrCreateAnonymousId = () => {
  let anonId = localStorage.getItem('aasc_anonymous_id');
  if (!anonId) {
    anonId = 'anon_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('aasc_anonymous_id', anonId);
  }
  return anonId;
};

// Get UTM parameters from URL
const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content')
  };
};

// Get landing page (first page user visited)
const getLandingPage = () => {
  let landingPage = sessionStorage.getItem('aasc_landing_page');
  if (!landingPage) {
    landingPage = window.location.pathname;
    sessionStorage.setItem('aasc_landing_page', landingPage);
  }
  return landingPage;
};

// Store UTM params on first visit
const storeUtmParams = () => {
  const utmParams = getUtmParams();
  if (utmParams.utm_source && !sessionStorage.getItem('aasc_utm_stored')) {
    sessionStorage.setItem('aasc_utm_source', utmParams.utm_source);
    sessionStorage.setItem('aasc_utm_medium', utmParams.utm_medium || '');
    sessionStorage.setItem('aasc_utm_campaign', utmParams.utm_campaign || '');
    sessionStorage.setItem('aasc_utm_content', utmParams.utm_content || '');
    sessionStorage.setItem('aasc_utm_stored', 'true');
  }
};

// Get stored UTM params
const getStoredUtmParams = () => {
  return {
    utm_source: sessionStorage.getItem('aasc_utm_source'),
    utm_medium: sessionStorage.getItem('aasc_utm_medium'),
    utm_campaign: sessionStorage.getItem('aasc_utm_campaign'),
    utm_content: sessionStorage.getItem('aasc_utm_content')
  };
};

/**
 * Track an analytics event
 * @param {string} eventType - Event type from the predefined list
 * @param {object} properties - Additional event properties
 */
export const trackEvent = async (eventType, properties = {}) => {
  // Initialize on first call
  storeUtmParams();
  getLandingPage();
  
  const sessionId = getOrCreateSessionId();
  const anonymousId = getOrCreateAnonymousId();
  const utmParams = getStoredUtmParams();
  
  const eventData = {
    event_type: eventType,
    session_id: sessionId,
    anonymous_id: anonymousId,
    page_url: window.location.pathname,
    referrer: document.referrer,
    landing_page: getLandingPage(),
    ...utmParams,
    ...properties
  };
  
  // Send to PostHog if available
  if (window.posthog) {
    try {
      window.posthog.capture(eventType, eventData);
    } catch (e) {
      console.debug('PostHog tracking failed:', e);
    }
  }
  
  // Send to backend
  try {
    await fetch(`${API_URL}/api/analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData)
    });
  } catch (e) {
    console.debug('Backend tracking failed:', e);
  }
};

/**
 * Track page view
 * @param {string} pageName - Name of the page
 * @param {object} properties - Additional properties
 */
export const trackPageView = (pageName, properties = {}) => {
  trackEvent('content_viewed', {
    content_slug: pageName,
    ...properties
  });
};

/**
 * Track CTA click
 * @param {string} ctaName - Name of the CTA
 * @param {string} ctaSource - Where the CTA was clicked
 * @param {object} properties - Additional properties
 */
export const trackCtaClick = (ctaName, ctaSource, properties = {}) => {
  trackEvent('primary_cta_clicked', {
    cta_source: ctaSource,
    metadata: { cta_name: ctaName },
    ...properties
  });
};

/**
 * Identify user after email capture
 * @param {string} email - User's email
 * @param {object} traits - Additional user traits
 */
export const identifyUser = (email, traits = {}) => {
  // Store email for future events
  sessionStorage.setItem('aasc_user_email', email);
  
  // Identify in PostHog
  if (window.posthog) {
    try {
      window.posthog.identify(email, traits);
    } catch (e) {
      console.debug('PostHog identify failed:', e);
    }
  }
  
  // Track lead capture
  trackEvent('lead_captured', {
    user_email: email,
    ...traits
  });
};

/**
 * Get stored user email
 */
export const getStoredUserEmail = () => {
  return sessionStorage.getItem('aasc_user_email');
};

// Event type constants for reference
export const EventTypes = {
  // Content
  CONTENT_VIEWED: 'content_viewed',
  
  // CTAs
  PRIMARY_CTA_CLICKED: 'primary_cta_clicked',
  SECONDARY_CTA_CLICKED: 'secondary_cta_clicked',
  
  // Assessment
  ASSESSMENT_STARTED: 'assessment_started',
  ASSESSMENT_COMPLETED: 'assessment_completed',
  ACTION_PLAN_GENERATED: 'action_plan_generated',
  ACTION_PLAN_VIEWED: 'action_plan_viewed',
  
  // Axiom
  AXIOM_OPENED: 'axiom_opened',
  AXIOM_MESSAGE_SENT: 'axiom_message_sent',
  AXIOM_WITH_CONTEXT: 'axiom_with_context',
  
  // Lead
  LEAD_CAPTURED: 'lead_captured',
  NURTURE_ENROLLED: 'nurture_enrolled',
  
  // Offers
  OFFER_VIEWED: 'offer_viewed',
  
  // Checkout - Documentation
  DOCUMENTATION_CHECKOUT_STARTED: 'documentation_checkout_started',
  DOCUMENTATION_PURCHASED: 'documentation_purchased',
  
  // Checkout - Assessment Product
  ASSESSMENT_PRODUCT_CHECKOUT_STARTED: 'assessment_product_checkout_started',
  ASSESSMENT_PRODUCT_PURCHASED: 'assessment_product_purchased',
  
  // Checkout - Declaration
  DECLARATION_STARTED: 'declaration_started',
  DECLARATION_CHECKOUT_STARTED: 'declaration_checkout_started',
  DECLARATION_PURCHASED: 'declaration_purchased',
  
  // Tools
  TOOL_STARTED: 'tool_started',
  TOOL_COMPLETED: 'tool_completed',
  
  // Retention
  RETURN_VISIT: 'return_visit'
};

export default {
  trackEvent,
  trackPageView,
  trackCtaClick,
  identifyUser,
  getStoredUserEmail,
  EventTypes
};
